// src/components/PurchaseOrders.js
import React, { useEffect, useMemo, useRef, useState, useImperativeHandle, useCallback } from "react";
import { useInstantListCache } from "../hooks/useInstantListCache";
import "../styles/Invoices.css"; // ✅ reuse same design/styles as Invoices
import "../styles/PurchaseOrders.css"; // ✅ PO-only modal fixes (scoped)
import { FaDownload, FaTrashAlt, FaRegCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import { getSocket } from "../lib/socket";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { useReactToPrint } from "react-to-print";
import PurchaseOrderPrint from "./PurchaseOrderPrint";

/* ===================== Helpers ===================== */
const formatDateDots = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d)) return dateStr; 
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}.${mm}.${yy}`;
};

const dateWithin = (dateStr, from, to) => {
  if (!dateStr || (!from && !to)) return true;
  const v = new Date(dateStr).setHours(0, 0, 0, 0);
  if (Number.isNaN(v)) return true;
  if (from && v < new Date(from).setHours(0, 0, 0, 0)) return false;
  if (to && v > new Date(to).setHours(0, 0, 0, 0)) return false;
  return true;
};

const toCSV = (rows) => {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    const s = (v ?? "").toString();
    const needs = /[",\n;]|\s$|^\s/.test(s);
    const e = s.replace(/"/g, '""');
    return needs ? `"${e}"` : e;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
};

const download = (filename, mime, content) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const tryShowPicker = (target) => {
  try {
    target?.showPicker?.();
    target?.focus?.();
  } catch (_) {
    // ignore (browser user gesture rules)
  }
};

// dates safe-ish
function safeISO(dateLike) {
  if (!dateLike) return undefined;
  const d = new Date(dateLike);
  if (Number.isNaN(d)) return undefined;
  return d.toISOString();
}

function parseYMD(dateLike) {
  if (!dateLike) return null;
  if (typeof dateLike === "string") {
    const m = dateLike.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return { y: m[1], m: m[2], d: m[3] };
  }
  const d = new Date(dateLike);
  if (Number.isNaN(d)) return null;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear());
  return { y: yy, m: mm, d: dd };
}

function formatDMY(dateLike) {
  const ymd = parseYMD(dateLike);
  return ymd ? `${ymd.d}-${ymd.m}-${ymd.y}` : "—";
}

const firstNonEmpty = (...cands) =>
  cands.find((v) => v != null && String(v).trim() !== "") ?? "";

const getPurchaseOrderStatusTheme = (status) => {
  switch (String(status || "DRAFT").toUpperCase()) {
    case "SENT":
      return {
        bg: "rgba(59, 130, 246, 0.16)",
        border: "rgba(59, 130, 246, 0.42)",
        color: "#bfdbfe",
      };
    case "ACCEPTED":
      return {
        bg: "rgba(168, 85, 247, 0.16)",
        border: "rgba(168, 85, 247, 0.42)",
        color: "#e9d5ff",
      };
    case "RECEIVED":
      return {
        bg: "rgba(20, 184, 166, 0.16)",
        border: "rgba(20, 184, 166, 0.42)",
        color: "#99f6e4",
      };
    case "VOID":
      return {
        bg: "rgba(239, 68, 68, 0.16)",
        border: "rgba(239, 68, 68, 0.42)",
        color: "#fecaca",
      };
    case "DRAFT":
    default:
      return {
        bg: "rgba(100, 116, 139, 0.18)",
        border: "rgba(148, 163, 184, 0.35)",
        color: "#dbe4ee",
      };
  }
};

const getPurchaseOrderStatusOptions = (T) => [
  { value: "DRAFT", label: T("status.draft", "Draft") },
  { value: "SENT", label: T("status.sent", "Sent") },
  { value: "ACCEPTED", label: T("status.accepted", "Accepted") },
  { value: "RECEIVED", label: T("status.received", "Received") },
  { value: "VOID", label: T("status.void", "Void") },
];

const buildPurchaseOrderStatusSelectStyles = (status) => {
  const theme = getPurchaseOrderStatusTheme(status);

  return {
    control: (base, state) => ({
      ...base,
      minHeight: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: theme.bg,
      borderColor: state.isFocused ? "#4953ff" : theme.border,
      boxShadow: state.isFocused ? "0 0 0 2px rgba(73,83,255,0.15)" : "none",
      cursor: "pointer",
      "&:hover": {
        borderColor: state.isFocused ? "#4953ff" : theme.border,
      },
    }),
    valueContainer: (base) => ({
      ...base,
      height: 34,
      padding: "0 8px 0 10px",
    }),
    singleValue: (base) => ({
      ...base,
      color: theme.color,
      fontSize: 13,
      fontWeight: 600,
    }),
    input: (base) => ({
      ...base,
      color: theme.color,
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: theme.color,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#cbd5e1",
      padding: 6,
      "&:hover": {
        color: "#ffffff",
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#0f1318",
      border: "1px solid #2a3547",
      borderRadius: 10,
      overflow: "hidden",
    }),
    menuList: (base) => ({
      ...base,
      padding: 6,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      borderRadius: 8,
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#2563eb"
        : state.isFocused
        ? "rgba(255,255,255,0.06)"
        : "transparent",
      color: "#e5e7eb",
    }),
  };
};

// ✅ Grid template for Purchase Orders table (NO "Total" column)
const PO_GRID = "120px 90px 110px 110px 1.2fr 1.2fr 1fr 160px 150px 110px";
//   PO ID  Load   Date   Due    Pickup  Delivery Supplier SubPrice Status Actions

/* ===================== Export Modal ===================== */
function ExportPurchaseOrdersModal({
  open,
  onClose,
  onConfirm,
  defaultFrom = "",
  defaultTo = "",
  count = 0,
}) {
  const { t } = useTranslation("purchase_orders");
  const T = (k, fallback, opts) => t(k, { defaultValue: fallback, ...(opts || {}) });

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [format, setFormat] = useState("csv"); // csv | xls | pdf
  
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFrom(defaultFrom || "");
      setTo(defaultTo || "");
      setFormat("csv");
    }
  }, [open, defaultFrom, defaultTo]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="export-modal-backdrop" onMouseDown={onClose}>
      <div
        className="invoices-modal po-export-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="invoices-modal__header">
          <h3 className="invoices-modal__title">{T("export.title", "Export Purchase Orders")}</h3>

          <button
            type="button"
            className="invoices-modal__close"
            onClick={onClose}
            aria-label={T("common.close", "Close")}
            title={T("common.close", "Close")}
          >
            ✕
          </button>
        </div>

        <div className="invoices-modal__body">
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>
            {T("export.scope", "Rows to export")}: <strong>{count ?? 0}</strong>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 6 }}>
            <div>
              <label style={{ display: "block", fontSize: 13, opacity: 0.85 }}>
                {T("export.date_from", "Date from")}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  ref={fromInputRef}
                  type="date"
                  className="load-history-input"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  onPointerDown={(e) => tryShowPicker(e.currentTarget)}
                  style={{ width: "100%", paddingRight: 32 }}
                />
                <FaRegCalendarAlt
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(fromInputRef.current);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(fromInputRef.current);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "40%",
                    transform: "translateY(-50%)",
                    pointerEvents: "auto",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                />
              </div>
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                {from ? formatDateDots(from) : ""}
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, opacity: 0.85 }}>
                {T("export.date_to", "Date until")}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  ref={toInputRef}
                  type="date"
                  className="load-history-input"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  onPointerDown={(e) => tryShowPicker(e.currentTarget)}
                  style={{ width: "100%", paddingRight: 32 }}
                />
                <FaRegCalendarAlt
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(toInputRef.current);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(toInputRef.current);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "40%",
                    transform: "translateY(-50%)",
                    pointerEvents: "auto",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                />
              </div>
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>{to ? formatDateDots(to) : ""}</div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 6 }}>
              {T("export.format", "Format")}
            </div>

            <div className="invoices-modal__options">
              {[
                { value: "csv", label: T("export.formats.csv", "CSV") },
                { value: "xls", label: T("export.formats.xls", "Excel (XLSX)") },
                { value: "pdf", label: T("export.formats.pdf", "PDF") },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`invoices-modal__option ${format === opt.value ? "is-active" : ""}`}
                  onClick={() => setFormat(opt.value)}
                >
                  <span>{opt.label}</span>
                  {format === opt.value && <span className="invoices-modal__check">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="invoices-modal__footer">
          <button className="load-history-btn" onClick={onClose}>
            {T("common.cancel", "Cancel")}
          </button>
          <button
            className="load-history-btn load-history-btn-primary"
            onClick={() => onConfirm({ from, to, format })}
            disabled={!count}
            style={!count ? { opacity: 0.6, pointerEvents: "none" } : undefined}
          >
            {T("export.export", "Export")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================== Filter Modal (simple) ===================== */
function FilterPurchaseOrdersModal({ open, value, onChange, suppliers = [], onApply, onClear, onClose }) {
  const { t } = useTranslation("purchase_orders");
  const T = (k, fallback, opts) => t(k, { defaultValue: fallback, ...(opts || {}) });

  const dateFromInputRef = useRef(null);
  const dateToInputRef = useRef(null);

  const statusOptions = [
    { value: "all", label: T("filter.status_all", "All") },
    { value: "DRAFT", label: T("status.draft", "Draft") },
    { value: "SENT", label: T("status.sent", "Sent") },
    { value: "ACCEPTED", label: T("status.accepted", "Accepted") },
    { value: "RECEIVED", label: T("status.received", "Received") },
    { value: "VOID", label: T("status.void", "Void") },
  ];

  const supplierSelectOptions = [
    { value: "", label: T("filter.any", "Any") },
    ...(suppliers || []).map((s) => ({ value: s, label: s })),
  ];

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="export-modal-backdrop" onMouseDown={onClose}>
      <div
        className="invoices-modal po-filter-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="invoices-modal__header">
          <h3 className="invoices-modal__title">{T("filter.title", "Filter Purchase Orders")}</h3>

          <button
            type="button"
            className="invoices-modal__close"
            onClick={onClose}
            aria-label={T("common.close", "Close")}
            title={T("common.close", "Close")}
          >
            ✕
          </button>
        </div>

        <div className="invoices-modal__body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 13, opacity: 0.85, marginBottom: 6 }}>
                {T("filter.status", "Status")}
              </label>
              <Select
                styles={customSelectStyles}
                options={statusOptions}
                value={statusOptions.find((o) => o.value === (value?.status || "all"))}
                onChange={(opt) => onChange((prev) => ({ ...prev, status: opt?.value || "all" }))}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isSearchable={false}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, opacity: 0.85, marginBottom: 6 }}>
                {T("filter.supplier", "Supplier")}
              </label>
              <Select
                styles={customSelectStyles}
                options={supplierSelectOptions}
                value={supplierSelectOptions.find((o) => o.value === (value?.supplier || ""))}
                onChange={(opt) => onChange((prev) => ({ ...prev, supplier: opt?.value ?? "" }))}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isSearchable
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, opacity: 0.85, marginBottom: 6 }}>
                {T("filter.date_from", "Date from")}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  ref={dateFromInputRef}
                  type="date"
                  className="load-history-input"
                  value={value?.dateFrom || ""}
                  onChange={(e) => onChange((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  onPointerDown={(e) => tryShowPicker(e.currentTarget)}
                  style={{ width: "100%", paddingRight: 32 }}
                />
                <FaRegCalendarAlt
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(dateFromInputRef.current);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(dateFromInputRef.current);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "40%",
                    transform: "translateY(-50%)",
                    pointerEvents: "auto",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, opacity: 0.85, marginBottom: 6 }}>
                {T("filter.date_to", "Date until")}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  ref={dateToInputRef}
                  type="date"
                  className="load-history-input"
                  value={value?.dateTo || ""}
                  onChange={(e) => onChange((prev) => ({ ...prev, dateTo: e.target.value }))}
                  onPointerDown={(e) => tryShowPicker(e.currentTarget)}
                  style={{ width: "100%", paddingRight: 32 }}
                />
                <FaRegCalendarAlt
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(dateToInputRef.current);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tryShowPicker(dateToInputRef.current);
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "40%",
                    transform: "translateY(-50%)",
                    pointerEvents: "auto",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="invoices-modal__footer">
          <button className="load-history-btn" onClick={onClear}>
            {T("filter.clear", "Clear")}
          </button>
          <button className="load-history-btn" onClick={onClose}>
            {T("common.cancel", "Cancel")}
          </button>
          <button className="load-history-btn load-history-btn-primary" onClick={onApply}>
            {T("filter.apply", "Apply")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================== Main Component ===================== */
const PurchaseOrders = React.forwardRef(function PurchaseOrders({ onStatsChange }, ref) {
  const { t } = useTranslation("purchase_orders");
  const T = (k, fallback, opts) => t(k, { defaultValue: fallback, ...(opts || {}) });

  const navigate = useNavigate();

  // ✅ adjust these endpoints to match your backend naming
  const API_BASE = "/purchaseorders";

  const PO_CACHE_KEY = "purchase_orders_cache_v1";

    const PAGE_LIMIT = 20;

  const {
    items: rows,
    setItems: setRows,
    loading,
    refresh: refreshPurchaseOrders,
    hasCache,
    meta,
    setMeta,
  } = useInstantListCache({
    cacheKey: PO_CACHE_KEY,
    enabled: true,
    fetcher: async ({ signal }) => {
      const { data } = await axios.get(`${API_BASE}`, {
        signal,
        params: { limit: PAGE_LIMIT, offset: 0 },
      });

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.purchaseOrders)
        ? data.purchaseOrders
        : [];

      const total =
        typeof data?.total === "number"
          ? data.total
          : typeof data?.count === "number"
          ? data.count
          : typeof data?.meta?.total === "number"
          ? data.meta.total
          : null;

      const nextOffset = list.length;
      const hasMore =
        typeof data?.hasMore === "boolean"
          ? data.hasMore
          : total != null
          ? nextOffset < total
          : list.length === PAGE_LIMIT;

      return { items: list, meta: { total: (typeof total === "number" ? total : 0), nextOffset, hasMore } };
    },
  });

  const [nextOffset, setNextOffset] = useState(() => meta?.nextOffset ?? rows.length ?? 0);
  const [hasMorePurchaseOrders, setHasMorePurchaseOrders] = useState(() => (meta?.hasMore ?? true));
  const [totalPurchaseOrders, setTotalPurchaseOrders] = useState(() => meta?.total ?? 0);
  const [loadingMore, setLoadingMore] = useState(false);

  // mantém paging coerente quando o hook hidrata do cache ou faz refresh
  useEffect(() => {
    if (!meta) return;
    if (typeof meta?.nextOffset === "number") setNextOffset(meta.nextOffset);
    if (typeof meta?.hasMore === "boolean") setHasMorePurchaseOrders(meta.hasMore);
    if (typeof meta?.total === "number") setTotalPurchaseOrders(meta.total);
  }, [meta]);

  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    supplier: "",
  });

  const [showExportModal, setShowExportModal] = useState(false);

  const [printPurchaseOrder, setPrintPurchaseOrder] = useState(null);
  const [printSupplier, setPrintSupplier] = useState(null);
  const [printMode, setPrintMode] = useState(null); // 'dialog' | 'download' | null
  const printRef = useRef(null);

  const [companyProfile, setCompanyProfile] = useState(null);
  const [companyLoaded, setCompanyLoaded] = useState(false);

  // ✅ expose functions to parent (Invoices toolbar)
  useImperativeHandle(ref, () => ({
    openFilter: () => setIsFilterModalOpen(true),
    openExport: () => setShowExportModal(true),

    // ✅ o Invoices.js consegue chamar isto
    setSearchTerm: (v) => setSearchTerm(String(v || "")),
  }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/company-details");
        const row = Array.isArray(data) ? data[0] : data;

        const base = {
          name: firstNonEmpty(row?.company_name, row?.name, row?.companyName),
          address: firstNonEmpty(
            row?.address,
            row?.address1,
            row?.street,
            row?.streetAddress,
            row?.street_address
          ),
          city: firstNonEmpty(row?.city, row?.town, row?.locality),
          postalCode: firstNonEmpty(
            row?.postal_code,
            row?.zipCode,
            row?.postalCode,
            row?.zip,
            row?.postcode
          ),
          country: firstNonEmpty(
            row?.country,
            row?.country_name,
            row?.countryName
          ),
          email: firstNonEmpty(
            row?.companyEmail,
            row?.company_email,
            row?.email,
            row?.contactEmail
          ),
          phone: firstNonEmpty(
            row?.companyPhoneNumber,
            row?.company_phone_number,
            row?.phoneNumber,
            row?.phone_number,
            row?.phone,
            row?.company_phone,
            row?.contactPhone
          ),
          logoUrl: firstNonEmpty(
            row?.photoCompany,
            row?.photo_company,
            row?.logo,
            row?.logoUrl
          ),
          paymentMethod: firstNonEmpty(row?.paymentMethod, "transferência"),
          iban: firstNonEmpty(row?.iban),
          bic: firstNonEmpty(row?.bic),
        };

        try {
          const { data: fi } = await axios.get("/company/financial-information");
          if (fi) {
            base.iban = firstNonEmpty(fi.iban, base.iban);
            base.bic = firstNonEmpty(fi.bic, fi.swift, base.bic);
          }
        } catch (fiErr) {
          console.warn("⚠️ Falhou a leitura de /company/financial-information:", fiErr);
        }

        setCompanyProfile(base);
      } catch (err) {
        console.warn("⚠️ Falhou a leitura do company profile:", err);
        setCompanyProfile(null);
      } finally {
        setCompanyLoaded(true);
      }
    })();
  }, []);

  const doPrettyPoPdf = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `purchase-order-${printPurchaseOrder?.poNumber ?? printPurchaseOrder?.id}`,
    onAfterPrint: () => {
      setPrintPurchaseOrder(null);
      setPrintSupplier(null);
      setPrintMode(null);
    },
  });

  useEffect(() => {
    if (!printPurchaseOrder || printMode !== "dialog") return;

    let attempts = 0;
    let cancelled = false;

    const tryPrint = () => {
      if (cancelled) return;
      if (printRef.current) {
        doPrettyPoPdf?.();
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        setTimeout(tryPrint, 50);
      } else {
        console.warn("PurchaseOrderPrint: ref ainda não disponível para imprimir.");
        setPrintPurchaseOrder(null);
        setPrintSupplier(null);
        setPrintMode(null);
      }
    };

    const t = setTimeout(tryPrint, 0);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [printPurchaseOrder, printMode, doPrettyPoPdf]);

  // Live updates (optional — safe even if backend doesn’t emit these)
  useEffect(() => {
    const s = getSocket();

    const subscribe = () => s.emit("subscribe:purchaseorders");
    subscribe();
    s.on("connect", subscribe);
    s.on("reconnect", subscribe);

    const shape = (r) => {
      const loadId = r.loadId ?? r.load_id ?? null;
      const loadNumber = r.loadNumber ?? r.load?.loadNumber ?? null;
      const loadDisplay = loadNumber ?? loadId ?? "—";

      return {
        id: r.id ?? null,
        poNumber: r.poNumber ?? r.purchaseOrderNumber ?? r.orderNumber ?? r.number ?? null,

        loadId,
        loadNumber,
        loadDisplay,

        issueDate: r.issueDate ?? r.date ?? r.createdAt ?? null,
        dueDate: r.paymentDueDate ?? r.dueDate ?? r.expectedDate ?? null,
        supplier: r.supplier ?? r.vendor ?? r.client ?? "—",
        pickup: r.pickupLocation ?? r.pickup ?? "—",
        delivery: r.deliveryLocation ?? r.delivery ?? "—",
        price4subcontrated: r.price4subcontrated ?? null,
        total:
          (r.price4subcontrated != null ? r.price4subcontrated : null) ??
          r.total ??
          r.price ??
          r.amount ??
          "—",
        status: r.status ?? "DRAFT",
      };
    };

    const onCreated = (po) =>
      setRows((prev) => {
        const shaped = shape(po);
        const key = String(shaped.id ?? shaped.poNumber);
        if (prev.some((x) => String(x.id ?? x.poNumber) === key)) return prev;
        return [shaped, ...prev];
      });

    // ✅ opcional: garante que cache/meta ficam sempre “limpinhos”
    // refreshPurchaseOrders();

    const onUpdated = (po) =>
      setRows((prev) => {
        const shaped = shape(po);
        const key = String(shaped.id ?? shaped.poNumber);
        return prev.map((x) => (String(x.id ?? x.poNumber) === key ? shaped : x));
      });

    const onDeleted = ({ id }) =>
      setRows((prev) => prev.filter((x) => String(x.id ?? x.poNumber) !== String(id)));

    s.on("purchaseorder:created", onCreated);
    s.on("purchaseorder:updated", onUpdated);
    s.on("purchaseorder:deleted", onDeleted);

    return () => {
      s.emit("unsubscribe:purchaseorders");
      s.off("connect", subscribe);
      s.off("reconnect", subscribe);
      s.off("purchaseorder:created", onCreated);
      s.off("purchaseorder:updated", onUpdated);
      s.off("purchaseorder:deleted", onDeleted);
    };
  }, [refreshPurchaseOrders]);

  // Normalize (for table)
  const normalized = useMemo(() => {
    return (rows || [])
      .filter((r) => !r?.revertedAt) // ✅ hide reverted POs
      .map((r) => {
        const loadId = r.loadId ?? r.load_id ?? null;
        const loadNumber = r.loadNumber ?? r.load?.loadNumber ?? null;
        const loadDisplay = loadNumber ?? loadId ?? "—";

        return {
          __raw: r, // ✅ guarda o row completo para prefill instantâneo no Edit

          id: r.id ?? null,
          poNumber: r.purchaseOrderNumber ?? r.poNumber ?? r.orderNumber ?? r.number ?? null,

          loadId,
          loadNumber,
          loadDisplay,

          date: r.issueDate ?? r.date ?? r.createdAt ?? null,
          dueDate: r.paymentDueDate ?? r.dueDate ?? null,

          supplier: r.client ?? r.supplier ?? r.vendor ?? "—",

          pickup: r.pickupLocation ?? r.pickup ?? "—",
          delivery: r.deliveryLocation ?? r.delivery ?? "—",

          price4subcontrated: r.price4subcontrated ?? null,

          total:
            (r.price4subcontrated != null ? r.price4subcontrated : null) ??
            r.price ??
            r.total ??
            r.amount ??
            "—",

          status: String(r.status ?? "DRAFT").toUpperCase(),
        };
      });
  }, [rows]);

  const supplierOptions = useMemo(
    () => [...new Set(normalized.map((x) => x.supplier).filter(Boolean))],
    [normalized]
  );

  const filtered = useMemo(() => {
    const term = (searchTerm || "").trim().toLowerCase();

    return normalized.filter((po) => {
      const matchesSearch =
        !term ||
        String(po.poNumber ?? "").toLowerCase().includes(term) ||
        String(po.id ?? "").toLowerCase().includes(term) ||
        String(po.loadDisplay ?? "").toLowerCase().includes(term) ||
        String(po.supplier ?? "").toLowerCase().includes(term) ||
        String(po.pickup ?? "").toLowerCase().includes(term) ||
        String(po.delivery ?? "").toLowerCase().includes(term);

      const matchesStatus =
        filterValues.status === "all" ||
        String(po.status || "DRAFT").toUpperCase() === String(filterValues.status).toUpperCase();

      const matchesSupplier = !filterValues.supplier || String(po.supplier) === String(filterValues.supplier);

      const matchesDate = dateWithin(po.date, filterValues.dateFrom, filterValues.dateTo);

      return matchesSearch && matchesStatus && matchesSupplier && matchesDate;
    });
  }, [normalized, searchTerm, filterValues]);

  const totalCount = Math.max(totalPurchaseOrders || 0, normalized.length);
  const visibleCount = filtered.length;

  const filtersActive =
    (filterValues?.status && filterValues.status !== "all") ||
    !!filterValues?.supplier ||
    !!filterValues?.dateFrom ||
    !!filterValues?.dateTo;
  
  const hasPOSearch = String(searchTerm || "").trim().length > 0;
  const hasPOSearchOrFilters = hasPOSearch || filtersActive;
  const hasAnyPurchaseOrders = normalized.length > 0;

  const companyDefaults = {
    name: T("defaults.company_name", "Company Name"),
    address: "",
    postalCode: "",
    city: "",
    country: "",
    phone: "",
    email: "",
    logoUrl: "",
    paymentMethod: T("defaults.payment_method", "transferência"),
    iban: "",
    bic: "",
  };

  const companyForPrint = { ...companyDefaults, ...(companyProfile || {}) };

  useEffect(() => {
    onStatsChange?.({ total: totalCount, visible: visibleCount, filtersActive });
  }, [totalCount, visibleCount, filtersActive, onStatsChange]);

  async function handleDelete(po) {
    const visibleId = po.id ?? po.poNumber;
    if (!visibleId) return alert(T("alerts.missing_id", "Missing Purchase Order ID"));

    if (!window.confirm(T("alerts.delete_confirm", "Delete Purchase Order {{id}}?", { id: visibleId }))) return;

    try {
      setDeletingId(visibleId);
      // optimistic
      setRows((prev) => prev.filter((x) => String(x.id ?? x.poNumber) !== String(visibleId)));

      if (po.id == null) throw new Error("Missing id for delete");
      await axios.delete(`${API_BASE}/${po.id}`);
    } catch (err) {
      console.error("❌ Delete failed:", err);
      alert(T("alerts.delete_failed", "Failed to delete Purchase Order"));
    } finally {
      setDeletingId(null);
    }
  }

  async function exportRowPdf(po) {
    if (!companyLoaded) {
      alert(T("alerts.company_loading", "Company data is still loading."));
      return;
    }

    const raw = po.__raw ?? po;

    const supplierForPrint = {
      name: firstNonEmpty(raw.client, raw.supplier, raw.vendor, po.supplier),
      address: firstNonEmpty(
        raw.supplierAddress,
        raw.address,
        raw.address1,
        raw.street,
        raw.streetAddress
      ),
      postalCode: firstNonEmpty(
        raw.supplierPostalCode,
        raw.postalCode,
        raw.postal_code,
        raw.zipCode,
        raw.zip
      ),
      city: firstNonEmpty(raw.supplierCity, raw.city, raw.town, raw.locality),
      country: firstNonEmpty(
        raw.supplierCountry,
        raw.country,
        raw.country_name,
        raw.countryName
      ),
      phone: firstNonEmpty(
        raw.supplierPhone,
        raw.phone,
        raw.phoneNumber,
        raw.phone_number,
        raw.contactPhone
      ),
      email: firstNonEmpty(
        raw.supplierEmail,
        raw.email,
        raw.contactEmail,
        raw.companyEmail
      ),
    };

    setPrintSupplier(supplierForPrint);
    setPrintMode("download");
    setPrintPurchaseOrder(raw);

    let attempts = 0;
    const waitForRef = () =>
      new Promise((resolve, reject) => {
        (function tick() {
          if (printRef.current) return resolve();
          attempts += 1;
          if (attempts > 80) {
            return reject(new Error("PurchaseOrderPrint não ficou pronto a tempo."));
          }
          setTimeout(tick, 25);
        })();
      });

    try {
      await waitForRef();

      const jsPDFmod = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");
      const jsPDF = jsPDFmod.jsPDF || jsPDFmod.default || jsPDFmod;

      const el = printRef.current;

      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        windowWidth: 800,
      });

      const imgData = canvas.toDataURL("image/png");

      const a4w = 595.28;
      const a4h = 841.89;

      const ratio = Math.min(a4w / canvas.width, a4h / canvas.height);
      const imgW = canvas.width * ratio;
      const imgH = canvas.height * ratio;
      const x = (a4w - imgW) / 2;
      const y = (a4h - imgH) / 2;

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      doc.addImage(imgData, "PNG", x, y, imgW, imgH);
      doc.save(`PO-${po.poNumber ?? po.id}.pdf`);
    } catch (err) {
      console.error("❌ Falha a gerar PDF da purchase order:", err);
      alert(T("export.install_pdf", "Failed to generate PDF."));
    } finally {
      setPrintPurchaseOrder(null);
      setPrintSupplier(null);
      setPrintMode(null);
    }
  }

  // Row click -> edit
  function handleRowClick(po, e) {
    if (
      e.target.classList.contains("invoices-action-icon") ||
      e.target.closest?.(".po-status-wrapper")
    )
      return;

    // ✅ usa SEMPRE o DB id para editar
    const id = po.id;
    if (!id) return alert(T("alerts.missing_id", "Missing Purchase Order ID"));

    const raw = po.__raw ?? po;

    navigate(`/purchaseorders/edit/${id}`, {
      state: {
        prefill: {
          id,

          // header / relations
          purchaseOrderNumber: raw.purchaseOrderNumber ?? raw.poNumber ?? po.poNumber ?? null,
          transportsessionId: raw.transportsessionId ?? raw.transportsession_id ?? null,
          loadId: raw.loadId ?? raw.load_id ?? po.loadId ?? null,

          // core
          client: raw.client ?? raw.supplier ?? raw.vendor ?? po.supplier ?? "",

          // optional date/hour (se existir no backend)
          date: raw.date ?? raw.optionalDate ?? null,
          hour: raw.hour ?? null,

          // pickup/delivery + timeline
          pickup: raw.pickup ?? po.pickup ?? "",
          delivery: raw.delivery ?? po.delivery ?? "",

          pickupDate: raw.pickupDate ?? null,
          pickupHour: raw.pickupHour ?? null,
          deliveryDate: raw.deliveryDate ?? null,
          deliveryHour: raw.deliveryHour ?? null,

          // pricing / VAT
          price: po.price4subcontrated ?? po.total ?? "",
          price4subcontrated: raw.price4subcontrated ?? po.price4subcontrated ?? "",
          vatRate: raw.vatRate ?? null,
          issuerCountry: raw.issuerCountry ?? "PT",

          // issue/payment
          issueDate: raw.issueDate ?? raw.date ?? po.date ?? null,
          paymentDueDate: raw.paymentDueDate ?? raw.dueDate ?? po.dueDate ?? null,

          // net days
          net_payment_days_carrier: raw.net_payment_days_carrier ?? null,

          // advanced (se existir)
          pickupCount: raw.pickupCount ?? 0,
          deliveryCount: raw.deliveryCount ?? 0,
          pickupLocation: raw.pickupLocation ?? null,
          deliveryLocation: raw.deliveryLocation ?? null,
          pickupList: raw.pickupList ?? null,
          deliveryList: raw.deliveryList ?? null,

          // status
          status: raw.status ?? po.status ?? "DRAFT",
        },
      },
    });
  }

  /* ---------- Export ---------- */
  const doExport = async ({ from, to, format }) => {
    const scope = filtered.filter((po) => dateWithin(po.date, from, to));
    if (!scope.length) return alert(T("export.no_rows", "No rows to export"));

    const flat = scope.map((p) => ({
      purchaseOrderId: p.poNumber ?? p.id,
      load: p.loadDisplay ?? "—",
      date: p.date,
      dueDate: p.dueDate,
      supplier: p.supplier,
      pickup: p.pickup,
      delivery: p.delivery,
      total: p.total,
      price4subcontrated: p.price4subcontrated ?? null,
      status: p.status || "DRAFT",
    }));

    const stamp = `${from || "all"}_to_${to || "all"}`;

    if (format === "csv") {
      download(`purchase_orders_${stamp}.csv`, "text/csv;charset=utf-8;", toCSV(flat));
      setShowExportModal(false);
      return;
    }

    if (format === "pdf") {
      try {
        const jsPDFmod = await import("jspdf");
        const autoTableMod = await import("jspdf-autotable");
        const jsPDF = jsPDFmod.default || jsPDFmod.jsPDF || jsPDFmod;
        const autoTable = autoTableMod.default || autoTableMod.autoTable || autoTableMod;

        const doc = new jsPDF();
        const head = [Object.keys(flat[0])];
        const body = flat.map((r) => Object.values(r));
        autoTable(doc, {
          head,
          body,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [1, 157, 238] },
          margin: 10,
        });
        doc.save(`purchase_orders_${stamp}.pdf`);
        setShowExportModal(false);
      } catch (e) {
        console.error(e);
        alert(T("export.install_pdf", "PDF export requires jsPDF + autotable"));
      }
      return;
    }

    // XLSX
    try {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(flat);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "PurchaseOrders");
      XLSX.writeFile(wb, `purchase_orders_${stamp}.xlsx`);
      setShowExportModal(false);
    } catch (e) {
      console.error(e);
      alert(T("export.install_xlsx", "Excel export requires xlsx"));
    }
  };

  const poSentinelRef = useRef(null);

  const fetchMorePOs = useCallback(async () => {
    if (loadingMore) return;
    if (!hasMorePurchaseOrders) return;

    try {
      setLoadingMore(true);

      const { data } = await axios.get(`${API_BASE}`, {
        params: { limit: PAGE_LIMIT, offset: nextOffset },
      });

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.purchaseOrders)
        ? data.purchaseOrders
        : [];

      const total =
        typeof data?.total === "number"
          ? data.total
          : typeof data?.count === "number"
          ? data.count
          : typeof data?.meta?.total === "number"
          ? data.meta.total
          : null;

      setRows((prev) => [...prev, ...list]);

      const newNextOffset = nextOffset + list.length;

      const newHasMore =
        typeof data?.hasMore === "boolean"
          ? data.hasMore
          : total != null
          ? newNextOffset < total
          : list.length === PAGE_LIMIT;

      setNextOffset(newNextOffset);
      setHasMorePurchaseOrders(!!newHasMore);
      if (typeof total === "number") setTotalPurchaseOrders(total);

      // ✅ se o hook suportar meta, atualiza também (mantém cache coerente)
      try {
        setMeta?.({ total: total ?? totalPurchaseOrders, nextOffset: newNextOffset, hasMore: !!newHasMore });
      } catch {}
    } catch (e) {
      console.error("❌ Failed to load more purchase orders:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [API_BASE, PAGE_LIMIT, nextOffset, hasMorePurchaseOrders, loadingMore, setRows, setMeta, totalPurchaseOrders]);

  useEffect(() => {
    if (loading) return;

    const el = poSentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        fetchMorePOs();
      },
      { root: null, rootMargin: "600px 0px", threshold: 0 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [loading, fetchMorePOs]);

  return (
    <>
      <div className="invoices-page">
        {/* Loading */}
        {/* Loading só se não houver cache (sem flash) */}
        {loading && rows.length === 0 && (
          <div className="invoices-empty">{T("common.loading", "Loading...")}</div>
        )}

        {/* Table (render sempre; cache aparece instant) */}
        {(!loading || rows.length > 0) && (
          <div className="invoices-table">
            {loading && rows.length > 0 && (
              <div className="invoices-loading" style={{ padding: "8px 0" }}>
                {T("common.loading", "Loading...")}
              </div>
            )}
            <div className="invoices-table__header" style={{ gridTemplateColumns: PO_GRID }}>
              <div>{T("table.po_id", "Purchase Order ID")}</div>
              <div>{T("table.load_id", "Load")}</div>
              <div>{T("table.date", "Date")}</div>
              <div>{T("table.due_date", "Due")}</div>
              <div>{T("table.pickup_place", "Pick-up")}</div>
              <div>{T("table.delivery_place", "Delivery")}</div>
              <div>{T("table.supplier", "Supplier")}</div>
              <div>{T("table.price4subcontrated", "Subcontracted Price")}</div>
              <div>{T("table.status", "Status")}</div>
              <div className="invoices-col-actions">{T("table.actions", "Actions")}</div>
            </div>

            {filtered.length > 0 ? (
              <>
                {filtered.map((po) => {
                  const rowId = po.poNumber ?? po.id;
                  return (
                    <div
                      className="invoices-row"
                      key={rowId}
                      onClick={(e) => handleRowClick(po, e)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && handleRowClick(po, e)}
                      style={{ position: "relative", gridTemplateColumns: PO_GRID }}
                    >
                    <div title={rowId}>{rowId}</div>

                    <div title={po.loadDisplay ?? "—"}>{po.loadDisplay ?? "—"}</div>

                    <div title={formatDMY(po.date)}>{formatDMY(po.date)}</div>
                    <div title={formatDMY(po.dueDate)}>{formatDMY(po.dueDate)}</div>

                    <div title={po.pickup}>
                      <div>{po.pickup}</div>
                      <div className="invoices-subline">{po.pickup !== "—" ? "" : ""}</div>
                    </div>

                    <div title={po.delivery}>
                      <div>{po.delivery}</div>
                      <div className="invoices-subline">{po.delivery !== "—" ? "" : ""}</div>
                    </div>

                    <div title={po.supplier}>{po.supplier}</div>

                    {/* Subcontracted Price */}
                    <div title={po.price4subcontrated ?? "—"}>
                      {po.price4subcontrated !== "—" &&
                      po.price4subcontrated !== "" &&
                      po.price4subcontrated != null &&
                      !Number.isNaN(Number(po.price4subcontrated))
                        ? Number(po.price4subcontrated).toFixed(2)
                        : po.price4subcontrated ?? "—"}
                    </div>

                    {/* Status */}
                    <div className="po-status-wrapper" onClick={(e) => e.stopPropagation()}>
                      <Select
                        classNamePrefix="po-status-react-select"
                        value={getPurchaseOrderStatusOptions(T).find(
                          (opt) => opt.value === String(po.status || "DRAFT").toUpperCase()
                        )}
                        options={getPurchaseOrderStatusOptions(T)}
                        isSearchable={false}
                        menuPlacement="bottom"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={buildPurchaseOrderStatusSelectStyles(po.status)}
                        onChange={async (selected) => {
                          const next = String(selected?.value || "DRAFT").toUpperCase();
                          try {
                            // optimistic
                            setRows((prev) =>
                              prev.map((x) =>
                                String(x.id ?? x.poNumber) === String(po.id ?? po.poNumber)
                                  ? { ...x, status: next }
                                  : x
                              )
                            );

                            if (po.id == null) throw new Error("Missing id for status update");
                            await axios.put(`${API_BASE}/${po.id}`, { status: next });
                          } catch (err) {
                            console.error("❌ Status update failed:", err);
                            // rollback
                            setRows((prev) =>
                              prev.map((x) =>
                                String(x.id ?? x.poNumber) === String(po.id ?? po.poNumber)
                                  ? { ...x, status: po.status }
                                  : x
                              )
                            );
                            alert(T("alerts.status_update_failed", "Failed to update status"));
                          }
                        }}
                      />
                    </div>

                    {/* Actions */}
                    <div className="invoices-cell invoices-cell--actions" onClick={(e) => e.stopPropagation()}>
                      {/* Export row */}
                      <button
                        type="button"
                        className="invoices-icon-btn invoices-icon-btn--export"
                        title={T("actions.export_row", "Export row")}
                        aria-label={T("actions.export_row", "Export row")}
                        onClick={() => exportRowPdf(po)}
                      >
                        <FaDownload />
                      </button>

                      {/* Delete */}
                      <button
                        type="button"
                        className="invoices-icon-btn invoices-icon-btn--danger"
                        title={T("actions.delete", "Delete")}
                        aria-label={T("actions.delete", "Delete")}
                        onClick={() => handleDelete(po)}
                        style={{
                          opacity: deletingId === (po.id ?? po.poNumber) ? 0.5 : 1,
                          pointerEvents: deletingId === (po.id ?? po.poNumber) ? "none" : "auto",
                        }}
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                );
              })}
                <div ref={poSentinelRef} style={{ height: 1 }} aria-hidden="true" />

                {loadingMore && (
                  <div className="invoices-loading" style={{ padding: "12px 0" }}>
                    {T("common.loading", "Loading...")}
                  </div>
                )}
              </>
            ) : (
              <div className="invoices-empty po-empty">
                <div className="invoices-empty__title po-empty__title">
                  {hasPOSearchOrFilters && hasAnyPurchaseOrders
                    ? T("empty.no_results_title", "No purchase orders found")
                    : T("empty.title", "There are no purchase orders yet")}
                </div>

                <div className="invoices-empty__subtitle po-empty__subtitle">
                  {hasPOSearchOrFilters && hasAnyPurchaseOrders
                    ? T(
                        "empty.no_results_subtitle",
                        "Try changing the search or clearing the applied filters."
                      )
                    : T(
                        "empty.subtitle",
                        "Create the first purchase order to start tracking subcontracted transport costs."
                      )}
                </div>

                {hasPOSearchOrFilters && hasAnyPurchaseOrders ? (
                  <button
                    className="invoices-btn invoices-btn--primary po-empty__button"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterValues({
                        status: "all",
                        dateFrom: "",
                        dateTo: "",
                        supplier: "",
                      });
                    }}
                    type="button"
                  >
                    {T("empty.clear_filters", "Clear search/filters")}
                  </button>
                ) : (
                  <button
                    className="invoices-btn invoices-btn--primary po-empty__button"
                    onClick={() => navigate("/purchaseorders/create")}
                    type="button"
                  >
                    {T("buttons.new_purchase_order", "+ New Purchase Order")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div
        style={{
          position: "fixed",
          left: -10000,
          top: 0,
          width: 794,
          background: "#ffffff",
        }}
      >
        {printPurchaseOrder && (
          <PurchaseOrderPrint
            ref={printRef}
            purchaseOrder={{
              poNumber:
                printPurchaseOrder.purchaseOrderNumber ??
                printPurchaseOrder.poNumber ??
                printPurchaseOrder.orderNumber ??
                printPurchaseOrder.id,

              date: safeISO(
                printPurchaseOrder.issueDate ??
                  printPurchaseOrder.date ??
                  printPurchaseOrder.createdAt
              ),

              deliveryDate: safeISO(
                printPurchaseOrder.deliveryDate ??
                  printPurchaseOrder.dueDate ??
                  printPurchaseOrder.paymentDueDate
              ),

              currency: printPurchaseOrder.currency || "EUR",
              vatRate: printPurchaseOrder.vatRate ?? 0,
              discount: printPurchaseOrder.discount ?? 0,
              paymentTerms:
                printPurchaseOrder.paymentTerms ??
                printPurchaseOrder.net_payment_days_carrier ??
                "",
              deliveryAddress: firstNonEmpty(
                printPurchaseOrder.deliveryAddress,
                printPurchaseOrder.delivery,
                printPurchaseOrder.deliveryLocation
              ),
              notes: printPurchaseOrder.notes || "",
              reference:
                printPurchaseOrder.reference ??
                printPurchaseOrder.loadNumber ??
                printPurchaseOrder.loadId ??
                "",

              items:
                Array.isArray(printPurchaseOrder.items) &&
                printPurchaseOrder.items.length
                  ? printPurchaseOrder.items
                  : [
                      {
                        description: `Subcontracted transport ${firstNonEmpty(
                          printPurchaseOrder.pickupLocation,
                          printPurchaseOrder.pickup,
                          "—"
                        )} → ${firstNonEmpty(
                          printPurchaseOrder.deliveryLocation,
                          printPurchaseOrder.delivery,
                          "—"
                        )}`,
                        qty: 1,
                        unitPrice: Number(
                          printPurchaseOrder.price4subcontrated ??
                            printPurchaseOrder.price ??
                            printPurchaseOrder.total ??
                            0
                        ),
                        total: Number(
                          printPurchaseOrder.price4subcontrated ??
                            printPurchaseOrder.price ??
                            printPurchaseOrder.total ??
                            0
                        ),
                      },
                    ],
            }}
            issuer={companyForPrint}
            supplier={printSupplier}
          />
        )}
      </div>
      
      {/* Modals */}
      <ExportPurchaseOrdersModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={doExport}
        defaultFrom={filterValues.dateFrom}
        defaultTo={filterValues.dateTo}
        count={filtered.filter((po) => dateWithin(po.date, filterValues.dateFrom, filterValues.dateTo)).length}
      />

      <FilterPurchaseOrdersModal
        open={isFilterModalOpen}
        value={filterValues}
        onChange={setFilterValues}
        suppliers={supplierOptions}
        onApply={() => setIsFilterModalOpen(false)}
        onClear={() =>
          setFilterValues({
            status: "all",
            dateFrom: "",
            dateTo: "",
            supplier: "",
          })
        }
        onClose={() => setIsFilterModalOpen(false)}
      />
    </>
  );
});

export default PurchaseOrders;
